import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Seq } from 'immutable';
import { isEmpty, isEqual, xorWith } from 'lodash-es';
import {
  BehaviorSubject,
  distinctUntilChanged,
  map,
  Subject,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs';
import { CompanyContextService } from 'src/app/shared/company-context.service';
import { TickService } from 'src/app/shared/tick.service';
import { AppState } from 'src/store/appstate';
import {
  PositionReportAlertViewModelRecord,
  selectPositionReportAlerts,
} from 'src/util/mapping/position-report-alert-model-record';
import { undefinedOrMap } from 'src/util/rxjs';
import { filterNull } from 'src/util/rxjs/operators/filter-null';

@Injectable({
  providedIn: 'root',
})
export class PositionReportAlertService implements OnDestroy {
  private _positionReportAlerts = new BehaviorSubject<
    Seq.Indexed<PositionReportAlertViewModelRecord>
  >(Seq.Indexed<PositionReportAlertViewModelRecord>());
  private _onDestory$ = new Subject();

  public get alerts$() {
    return this._positionReportAlerts.asObservable();
  }

  public get count$() {
    return this._positionReportAlerts.pipe(map((alerts) => alerts.count()));
  }

  constructor(tick: TickService, store: Store<AppState>, companyContext: CompanyContextService) {
    const company$ = companyContext.company$.pipe(
      undefinedOrMap((company) => company.id),
      filterNull(),
    );
    tick.everyFiveSeconds$
      .pipe(
        withLatestFrom(company$),
        switchMap(([_, companyId]) => store.select(selectPositionReportAlerts(companyId))),
        distinctUntilChanged((a1, a2) => isEmpty(xorWith(a1.toArray(), a2.toArray(), isEqual))),
        takeUntil(this._onDestory$),
      )
      .subscribe(this._positionReportAlerts);
  }

  public ngOnDestroy(): void {
    this._onDestory$.next(null);
    this._onDestory$.complete();
  }
}
